/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import GroupProperties, {getAssetTypes} from '@ion/core/src/services/GroupProperties';
import {useRefreshState} from '@ion/dls/src/hooks/useRefreshState';
import {FilterOption, FilterPropsType} from '../types/types';
import Filter from './Filter';
import {createFilterOptions, getMenuStyles} from './FilterHelpers';
import {FilterParamsProps} from './useFilterSubmit';

/**
 * Defines the prop definitions for AssetTypeFilter ...
 *
 * accountId:      The ID of currently selected account.
 * loadAssetTypes: Override function to load the default options data.
 */
export interface AssetTypeFilterProps extends FilterPropsType {
  accountId: number;
  loadAssetTypes?: Function;
};

/**
 * The AssetTypeFilter component is a reusable filter for managing the filtering
 * of asset types, and can be custom-styled with props.
 *
 * ```typescript
 *
 * import AssetTypeFilter from '@ion/dls/src/filters';
 *
 * <AssetTypeFilter elementId={elementId}
 *   name={name}
 *   accountId={accountId}
 *   filterState={filterState}
 *   loadAssetTypes={loadAssetTypes}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   onMenuClose={onMenuClose}
 *   primaryButtonDisabled={primaryButtonDisabled}
 *   secondaryButtonDisabled={secondaryButtonDisabled}
 *   showSelectedOptionsCount={false}
 *   isMobileView= {false} />
 *
 * ```
 */
export const AssetTypeFilter = ({
  elementId,
  name,
  accountId,
  loadAssetTypes,
  onSubmit,
  onCancel,
  onMenuClose,
  primaryButtonDisabled,
  secondaryButtonDisabled,
  filterState = {},
  showSelectedOptionsCount = true,
  isMobileView = false
}: AssetTypeFilterProps) => {
  const {refresh, updateRefresh} = useRefreshState(accountId);
  const {
    loading: isLoading,
    error: hasErrors,
    selected: selectedOptions
  }                             = filterState;
  const {
    menuLeft,
    menuWidth,
    contentGridTemplateColumns,
    contentJustifyContent,
    contentGridColumnGap,
    contentGridRowGap
  } = getMenuStyles(isMobileView);

  function handleSubmit({selections, options, loading, hasError}) {
    const filterParams: FilterParamsProps = {
      optionsKey    : FilterOption.value,
      selectionsKey : FilterOption.value,
      options,
      selections,
      loading,
      hasError
    };
    onSubmit?.(name, filterParams);
  };

  const loadFilterOptions = useCallback(async(accountId) => {
    await GroupProperties.getInheritedProperties(accountId);
    const assetTypes = await getAssetTypes();
    return {
      result       : assetTypes,
      filterResult : createFilterOptions(assetTypes)
    };
  }, []);

  return (
    <Filter optionType={translate('common:filters.assetType')}
      optionPropId={FilterOption?.label}
      optionPropLabel={FilterOption?.value}
      selectedOptionIds={selectedOptions ?? []}
      loading={isLoading}
      showSelectedOptionsCount={showSelectedOptionsCount}
      primaryButtonDisabled={primaryButtonDisabled}
      secondaryButtonDisabled={secondaryButtonDisabled}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      onMenuClose={onMenuClose}
      elementId={elementId}
      menuLeft={menuLeft}
      menuWidth={menuWidth}
      contentGridTemplateColumns={contentGridTemplateColumns}
      contentJustifyContent={contentJustifyContent}
      contentGridColumnGap={contentGridColumnGap}
      contentGridRowGap={contentGridRowGap}
      loadFilterOptions={loadFilterOptions}
      accountId={accountId}
      isMobileView={isMobileView}
      refresh={refresh}
      updateRefresh={updateRefresh} />
  );
};

export default AssetTypeFilter;
